import { useCurrentBrandQuery } from '@/hooks/queries/use-current-brand-query';
import { invalidateDashboardOverview } from '@/hooks/queries/dashboard-overview-keys';
import type { QueryClient } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

/** Base path for every brand-side gifting endpoint. */
export const GIFTS_PATH = '/dashboard/gifts';

/**
 * Query-key factory for the brand's gifts, keyed on brand so two brands of one
 * organization cache independently (gabarit: `collab-keys.ts`). Filter, search
 * and page all resolve server-side, so each filtered page lives under its own
 * list key; the bare list key is their prefix.
 */
export const giftKeys = {
  list: (brandId: string, params?: Record<string, unknown>) =>
    params && Object.keys(params).length > 0
      ? (['gifts', brandId, 'list', params] as const)
      : (['gifts', brandId, 'list'] as const),
  /**
   * The locale is part of the key because it is part of the request: the
   * detail carries a ready-to-send reminder written in it, and a FR draft
   * served to an EN dashboard is a draft nobody sends. Omitting it yields the
   * prefix every localized entry lives under, which is what the invalidator
   * wants: one gesture stales the gift in every language at once.
   */
  detail: (brandId: string, giftId: string, locale?: string) =>
    locale
      ? (['gifts', brandId, 'detail', giftId, locale] as const)
      : (['gifts', brandId, 'detail', giftId] as const),
};

/**
 * Everything a gift mutation stales. The counters travel in the list `meta`,
 * so an action answering with the detail must stale the list too, or the
 * filter pills keep quoting the world as it was before the gesture.
 */
export const invalidateGiftQueries = (
  queryClient: QueryClient,
  brandId: string,
  giftId?: string
): void => {
  queryClient.invalidateQueries({ queryKey: giftKeys.list(brandId) });
  // The dashboard to-do card counts the blocked gifts the list counts.
  invalidateDashboardOverview(queryClient);
  if (giftId) {
    queryClient.invalidateQueries({ queryKey: giftKeys.detail(brandId, giftId) });
  }
};

/**
 * Shared mutation plumbing: resolves the current brand and returns the
 * invalidator for one gift, so action hooks never copy the brand resolution.
 */
export const useInvalidateGiftQueries = (): ((giftId?: string) => void) => {
  const queryClient = useQueryClient();
  const { data: brand } = useCurrentBrandQuery();
  const brandId = brand?.id ?? '';
  return useCallback(
    (giftId?: string) => invalidateGiftQueries(queryClient, brandId, giftId),
    [queryClient, brandId]
  );
};
